import { Button, Panel } from "@mixitone/components";
import { Club, ClubDocument, ClubPlayerAcceptedDocument } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { withStateToggle } from "@mixitone/util";
import { DashLayout } from "Components/DashLayout";
import { ClubController } from "controllers/ClubController";
import { PortalController } from "controllers/PortalController";
import { PdfView } from "./PdfView";
import TickIcon from "@mixitone/icons/tick.svg";
import ViewIcon from "@mixitone/icons/edit.svg";
import { actions } from "@mixitone/actions";
import { AuthController } from "controllers/AuthController";

interface State {
  loadingDocuments: boolean;
  acceptingInvite: boolean;
  acceptedDocuments: Record<string, ClubPlayerAcceptedDocument>;
  clubDocuments: ClubDocument[];
  documentsToReview: ClubDocument[];
  otherDocuments: ClubDocument[];
  showDocument: ClubDocument | null;
}

class ClubInvitePageController extends PortalController<State> {
  get initialState(): State {
    return {
      loadingDocuments: true,
      acceptingInvite: false,
      acceptedDocuments: {},
      clubDocuments: [],
      documentsToReview: [],
      otherDocuments: [],
      showDocument: null,
    };
  }

  async initialize() {
    await this.loadClubDocuments();
  }

  @withStateToggle("loadingDocuments")
  async loadClubDocuments() {
    const club = this.get(ClubController).state.club;
    const clubPlayer = this.get(ClubController).state.clubPlayer;

    const [documents, acceptedDocuments] = await Promise.all([
      await ClubDocument.query().eq("club_id", club.id).all(),
      await ClubPlayerAcceptedDocument.query().eq("club_player_id", clubPlayer.id).all(),
    ]);

    this.state.clubDocuments = ClubDocument.onlyLastVersion(documents);
    this.state.documentsToReview = this.state.clubDocuments.filter((doc) => doc.requiresAcceptance);
    this.state.otherDocuments = this.state.clubDocuments.filter((doc) => !doc.requiresAcceptance);

    for (const doc of this.state.documentsToReview) {
      const acceptances = acceptedDocuments.filter(
        (ad) => ad.document_name === doc.name && ad.version === doc.version,
      );
      this.state.acceptedDocuments[doc.id!] = acceptances[0];
    }
  }

  actionShowDocument(document: ClubDocument) {
    this.state.showDocument = document;
  }

  async actionHideDocument(accepted: boolean) {
    if (!this.state.showDocument) return;
    const doc = this.state.showDocument;
    this.state.showDocument = null;

    const player = this.get(ClubController).state.clubPlayer;
    const existing = this.state.acceptedDocuments[doc.id!];

    if (accepted) {
      if (!existing) {
        const newAcceptance = new ClubPlayerAcceptedDocument({
          document_name: doc.name!,
          series_id: doc.series_id,
          club_document_id: doc.id,
          version: doc.version,
          club_player_id: player.id,
          account_id: player.account_id,
          source: "self",
          recorded_by_user_id: this.get(AuthController).state.user?.id,
          accepted_at: new Date().toISOString(),
        });
        await newAcceptance.save();
        this.state.acceptedDocuments[doc.id!] = newAcceptance;
      }
    } else {
      if (existing) {
        await existing.destroy();
        delete this.state.acceptedDocuments[doc.id!];
      }
    }
  }

  async actionAcceptInvite() {
    this.state.acceptingInvite = true;

    const unacceptedDocuments = this.state.documentsToReview.filter(
      (doc) => !this.state.acceptedDocuments[doc.id!],
    );
    if (unacceptedDocuments.length > 0) {
      alert("Please accept all required documents before proceeding.");
      return;
    }

    try {
      await actions.playerPortal.acceptClubInvite({
        clubId: this.get(ClubController).state.clubId,
      });
      await this.get(AuthController).loadClubPlayers();

      this.navigate(`/in/club/${this.get(ClubController).state.clubId}`, {
        state: {
          message: `You are now a member of ${this.get(ClubController).state.club.name}`,
        },
      });
    } catch (error) {
      alert("Failed to accept club invitation. Please try again.");
    }
  }
}

const Document = ApplicationView(({ document, required }: { document: ClubDocument; required?: boolean }) => {
  const controller = ClubInvitePageController.use();
  const { acceptedDocuments } = controller.state;
  const accepted = acceptedDocuments[document.id!];

  return (
    <Button kind="link" className="w-full" onClick={() => controller.actionShowDocument(document)}>
      <span className="flex items-center justify-between w-full">
        <span>{document.name}</span>
        {required && (
          <span>
            {accepted ? (
              <TickIcon className="ml-2 inline-block w-[18px] stroke-green-600" />
            ) : (
              <ViewIcon className="ml-2 inline-block w-[18px] stroke-black" />
            )}
          </span>
        )}
      </span>
    </Button>
  );
});

const ControlledClubInvitePage: React.FC = () => {
  const clubController = ClubController.use();
  const { club } = clubController.state;
  const controller = ClubInvitePageController.use();
  const { documentsToReview, otherDocuments, showDocument, acceptedDocuments, acceptingInvite } =
    controller.state;
  const unacceptedDocuments = documentsToReview.filter((doc) => !acceptedDocuments[doc.id!]);

  const acceptButton = (
    <Button
      onClick={() => controller.actionAcceptInvite()}
      disabled={acceptingInvite || unacceptedDocuments.length > 0}
      spinner={acceptingInvite}
    >
      Accept invite
    </Button>
  );

  return (
    <DashLayout title={club.name}>
      <div className="flex flex-col gap-6 pb-6">
        <Panel title="Club details" titleClassName="font-semibold">
          <p>You've been invited to be a member of {club.name}.</p>
          {documentsToReview.length === 0 && <div className="flex justify-end mt-4">{acceptButton}</div>}
        </Panel>
        {documentsToReview.length > 0 && (
          <Panel title="Documents to review" titleClassName="font-semibold">
            <p>Please review the following documents before accepting your invitation:</p>
            <div className="w-full">
              {documentsToReview.length > 0 ? (
                documentsToReview.map((doc) => <Document key={doc.id} document={doc} required />)
              ) : (
                <p>No documents to review.</p>
              )}
            </div>
            <div className="flex justify-end mt-4">{acceptButton}</div>
          </Panel>
        )}
        {otherDocuments.length > 0 && (
          <Panel title="Other documents" titleClassName="font-semibold">
            {otherDocuments.map((doc) => (
              <Document key={doc.id} document={doc} />
            ))}
          </Panel>
        )}
      </div>

      {showDocument && (
        <PdfView
          document={showDocument}
          required={documentsToReview.some((doc) => doc.id === showDocument.id)}
          onClose={(accepted) => controller.actionHideDocument(accepted)}
        />
      )}
    </DashLayout>
  );
};

export const ClubInvitePage = ClubInvitePageController.scope(ApplicationView(ControlledClubInvitePage));
